import React, { useState, useEffect } from "react";
import Navbar from "../Navbar";
import Footer from "../Footer";
import Categories from "./Categories";
import { Link, useLocation } from "react-router-dom";
import { div } from "framer-motion/client";
import Constant from "../../utils/Constant"

const Product = () => {
  const location = useLocation();
  const [searchQuery, setSearchQuery] = useState(
    location.state?.searchQuery || ""
  );
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedChildCategory, setSelectedChildCategory] = useState(null);
  const [selectedSubChildCategory, setSelectedSubChildCategory] = useState(null);
  const [products, setProducts] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [productNo, setProductNo] = useState(0);
  const ITEMS_PER_PAGE = 12;
  const [start, setStart] = useState(0);
  const [end, setEnd] = useState(0);
  const [isNewProductActive, setIsNewProductActive] = useState(false);
  
  // Animation states
  const [pageLoaded, setPageLoaded] = useState(false);
  const [contentVisible, setContentVisible] = useState(false);
  const [new1, setNew1] = useState(false);
  
  // Added for debugging
  const [debug, setDebug] = useState({
    requestUrl: "",
    responseStatus: "",
    responseData: null
  });
  
  // Initialize page load animation
  useEffect(() => {
    const timer = setTimeout(() => {
      setPageLoaded(true);
      setTimeout(() => setContentVisible(true), 200);
    }, 100);
    
    return () => clearTimeout(timer);
  }, []);

  // Enhanced parseImages function with better path cleaning and validation
  const parseImages = (imgData) => {
    // If imgData is null, undefined, or empty, return empty string
    if (!imgData || imgData === '' || imgData === 'null') {
      return "";
    }
    
    // If imgData is already a string and looks like a single path
    if (typeof imgData === "string") {
      // Try to parse as JSON first
      try {
        const parsed = JSON.parse(imgData);
        
        // If successfully parsed and it's an array with elements
        if (Array.isArray(parsed) && parsed.length > 0) {
          // Get the first valid image path
          const firstImage = parsed[0];
          if (firstImage && typeof firstImage === 'string') {
            return cleanImagePath(firstImage);
          }
        }
      } catch (parseError) {
        // If parsing fails, treat as direct path
        console.log('Direct path (not JSON):', imgData);
        return cleanImagePath(imgData);
      }
    }
    
    // If imgData is already an array
    if (Array.isArray(imgData) && imgData.length > 0) {
      const firstImage = imgData[0];
      if (firstImage && typeof firstImage === 'string') {
        return cleanImagePath(firstImage);
      }
    }
    
    // If all else fails, return empty string
    return "";
  };

  // Helper function to clean image paths
  const cleanImagePath = (path) => {
    if (!path || typeof path !== 'string') {
      return "";
    }
    
    // Remove unwanted characters like quotes, brackets, backslashes
    let cleanPath = path
      .replace(/[\[\]\"\\]/g, "") // Remove [, ], ", \
      .replace(/^\s+|\s+$/g, "") // Trim whitespace
      .replace(/^\/+/, "/"); // Ensure single leading slash
    
    // Ensure the path starts with a forward slash if it doesn't already
    if (cleanPath && !cleanPath.startsWith('/')) {
      cleanPath = '/' + cleanPath;
    }
    
    return cleanPath;
  };

  const newProducts = () => {
    setSelectedCategory(null);
    setSelectedChildCategory(null);
    setSelectedSubChildCategory(null);
    setSearchQuery("");
    setCurrentPage(1);
    setNew1(true);
  }
  
  const handleAllItemsClick = () => {
    setSearchQuery("");
   
     setSelectedCategory("");
    setSelectedChildCategory("");
    setSelectedSubChildCategory("");
    setCurrentPage(1);
    setNew1(false);

  };
  
  // Listen for search updates from navbar
  useEffect(() => {
    const handleSearchUpdate = (event) => {
      setSearchQuery(event.detail);
      setCurrentPage(1); // Reset to first page when search is updated from navbar
    };
    window.addEventListener("updateProductSearch", handleSearchUpdate);
    return () => {
      window.removeEventListener("updateProductSearch", handleSearchUpdate);
    };
  }, []);
  
  // Scroll to top when page changes
  useEffect(() => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  }, [currentPage]);

  // Main data fetching logic
  useEffect(() => {
    const fetchProducts = async () => {
      setIsLoading(true);
      setError(null);
    
      try {
        let combinedData = [];
        let url;
    
        // If we have a search query, use the search API
        if (searchQuery) {
           
          url = `${Constant.BASE_URL}/api/v1/search/${encodeURIComponent(searchQuery)}?page=${currentPage}`;
        }
        // If no search but we have categories, use the appropriate category API
        else if (selectedSubChildCategory) {
          url = `${Constant.BASE_URL}/api/v1/get-sub-child-products-child?main_category_id=${selectedCategory}&child_category_id=${selectedChildCategory}&sub_child_category_id=${selectedSubChildCategory}&page=${currentPage}`;
        } else if (selectedChildCategory) {
          url = `${Constant.BASE_URL}/api/v1/get-child-products-main?main_category_id=${selectedCategory}&child_category_id=${selectedChildCategory}&page=${currentPage}`;
        } else if (selectedCategory) {
          url = `${Constant.BASE_URL}/api/v1/get-main-products-Id?main_category_id=${selectedCategory}&page=${currentPage}`;
          console.log(`Fetching products for main category ID: ${selectedCategory}`);
          
          // Add an indicator to help with deb
          window.latestProductsRequested = true;
        } 
        else if(new1){
          url = `${Constant.BASE_URL}/api/v1/latest-products?page=${currentPage}`;
        }
        else {
          // Otherwise, get all products
          url = `${Constant.BASE_URL}/api/v1/get-total-products?page=${currentPage}`;
        }
        
        // For debugging
        setDebug(prev => ({...prev, requestUrl: url}));
    
        // Make API request
        const response = await fetch(url);
        
        // For debugging
        setDebug(prev => ({...prev, responseStatus: response.status}));
        
        if (!response.ok) {
          throw new Error(`Network response was not ok (Status: ${response.status})`);
        }
        
        const data = await response.json();
        
        // For general debugging
        setDebug(prev => ({...prev, responseData: data}));
        console.log("API Response:", data);
        
        // Extract products array from API response
        let productsArray = [];
        
        if (data.data && Array.isArray(data.data)) {
          productsArray = data.data;
        } else if (data.products && Array.isArray(data.products)) {
          productsArray = data.products;
        } else if (Array.isArray(data)) {
          productsArray = data;
        } else {
          // Look for any array property in the response
          const arrayProps = Object.keys(data).filter(key => Array.isArray(data[key]));
          if (arrayProps.length > 0) {
            productsArray = data[arrayProps[0]];
          }
        }
        
        // Extract pagination metadata
        let totalItems = 0;
        let lastPage = 1;
        
        // Check if we have meta information
        if (data.meta) {
          // Format {"current_page": 1, "from": 1, "last_page": 2, "per_page": 12, "to": 12, "total": 20}
          totalItems = data.meta.total || 0;
          lastPage = data.meta.last_page || 1;
          console.log("Pagination from meta:", {
            current: data.meta.current_page,
            lastPage: data.meta.last_page,
            total: data.meta.total
          });
        } else if (data.total !== undefined) {
          // Some APIs return total directly
          totalItems = data.total;
          lastPage = Math.ceil(totalItems / ITEMS_PER_PAGE);
          console.log("Pagination from total:", {
            lastPage,
            total: totalItems
          });
        } else if (data.pagination) {
          // Some APIs might have a pagination object
          totalItems = data.pagination.total || 0;
          lastPage = data.pagination.last_page || Math.ceil(totalItems / ITEMS_PER_PAGE);
        } else {
          // If no metadata, use the length of the array (assuming it's all data)
          totalItems = productsArray.length;
          lastPage = 1; // Assume only one page if no pagination info
        }
        
        // Set pagination state
        setTotalPages(lastPage || 1); // Ensure at least 1 page even if empty
        setProductNo(totalItems);
        
        // Map products with enhanced image parsing
        const mappedProducts = productsArray.map((product) => {
          let imagePath = "";

          // Try all possible image field names with enhanced parsing
          const imageFields = [
            'product_images',
            'child_product_images', 
            'sub_child_product_images',
            'images'
          ];
          
          // Try each field until we find a valid image
          for (const field of imageFields) {
            if (product[field]) {
              imagePath = parseImages(product[field]);
              if (imagePath) {
                console.log(`Found image in ${field}:`, imagePath);
                break; // Stop at first valid image found
              }
            }
          }
          
          // Log for debugging
          if (!imagePath) {
            console.log('No valid image found for product:', product.name || 'Unknown');
          }

          // Extract product info from all possible field names
          // FIXED: Ensure we have a valid id for each product
          const id = product.product_sequence_id || 
                     product.child_product_sequence_id || 
                     product.sub_child_product_sequence_id || 
                     product.id ||
                     product.product_id ||  // Added product_id as a possible field
                     0;  // Use 0 as fallback to prevent undefined IDs
                     
          const name = product.product_name || 
                       product.child_product_name || 
                       product.sub_child_product_name || 
                       product.name || 
                       product.model_name || 
                       product.title || 
                       "Unnamed Product";
                     
          // FIXED: Ensure we always have a valid type
          // Default to "product" if type is not specified
          const type = product.type || 
                      (product.product_sequence_id ? "product" : 
                       product.child_product_sequence_id ? "child_product" : 
                       product.sub_child_product_sequence_id ? "sub_child_product" : 
                       "product");
          
          // Convert latest_product and eol_product to boolean
          let latestProductValue = false;
          let eolProductValue = false;
          
          // Check if latest_product exists and convert it to a proper boolean
          if (product.latest_product !== undefined) {
            // Handle different data formats (string "true", number 1, etc.)
            latestProductValue = product.latest_product === true || 
                                product.latest_product === "true" || 
                                product.latest_product === 1 || 
                                product.latest_product === "1";
          }
          
          // Check if eol_product exists and convert it to a proper boolean
          if (product.eol_product !== undefined) {
            // Handle different data formats
            eolProductValue = product.eol_product === true || 
                             product.eol_product === "true" || 
                             product.eol_product === 1 || 
                             product.eol_product === "1";
          }
          
          // FIXED: Log product data for debugging
          console.log(`Mapped Product - ID: ${id}, Type: ${type}, Name: ${name}, Image: ${imagePath}`);
          
          return {
            id, // This will now always be defined
            name,
            image: imagePath,
            type, // This will now always have a value
            latest_product: latestProductValue,
            eol_product: eolProductValue,
            // Add any other fields that might be useful
            model_name: product.model_name || null,
            description: product.description || product.product_description || null
          };
        });
        
        // FIXED: Filter out any products with invalid IDs (just in case)
        const validProducts = mappedProducts.filter(product => product.id);
        console.log(`Filtered ${mappedProducts.length - validProducts.length} products with invalid IDs`);
        
        setProducts(validProducts);
      } catch (error) {
        console.error("Fetch error:", error);
        setError(error.message);
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchProducts();
  }, [selectedCategory, selectedChildCategory, selectedSubChildCategory, currentPage, searchQuery, new1]);
  
  // Update the useEffect that sets start and end values
  useEffect(() => {
    if (productNo > 0) {
      setStart((currentPage - 1) * ITEMS_PER_PAGE + 1);
      setEnd(Math.min(currentPage * ITEMS_PER_PAGE, productNo));
    } else {
      setStart(0);
      setEnd(0);
    }
  }, [currentPage, productNo]);
  
  const handleCategorySelect = (category, level) => {
    setSearchQuery(""); // Clear search when selecting category
    setNew1(false); // Reset new1 state when selecting category
    
    console.log("Category selected:", category, "Level:", level);
  
    switch (level) {
      case "main":
        setSelectedCategory(category.main_category_id);
        setSelectedChildCategory(null);
        setSelectedSubChildCategory(null);
        break;
      case "child":
        setSelectedChildCategory(category.child_category_id);
        setSelectedSubChildCategory(null);
        break;
      case "subchild":
        setSelectedSubChildCategory(category.sub_child_category_id);
        break;
      default:
        break;
    }
  
    setCurrentPage(1);
  };
  
  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };
  
  // Handle search input change
  const handleSearchInput = (e) => {
    setSelectedCategory("");
    setSelectedChildCategory("");
    setSelectedSubChildCategory("");
    setCurrentPage(1);
    setNew1(false);
    setSearchQuery(e.target.value);
     

    // Don't reset page here - we'll handle that in the search submit
  };
  
  // Handle search form submission
  const handleSearchSubmit = (e) => {
    e.preventDefault();
    setCurrentPage(1);  // Reset to first page when submitting search
    setNew1(false); // Reset new1 state when searching
    
    // Clear category selections when searching
    if (searchQuery) {
      setSelectedCategory(null);
      setSelectedChildCategory(null);
      setSelectedSubChildCategory(null);
    }
  };
  
  return (
    <div className={`page-container ${pageLoaded ? 'page-loaded' : ''}`}>
      <div className="navbar-container">
        <Navbar />
      </div>
      
      <div className={`main-content ${contentVisible ? 'content-visible' : ''}`}>
        <div className="max-w-7xl mx-auto justify-items-normal px-4 py-8 2xl:max-w-[2000px] h-[100%] items-start overflow-auto">
          <div className="flex flex-col md:flex-row gap-6 md:gap-8 2xl:gap-12">
            
            <div className="categories-sidebar sticky-sidebar w-full md:w-auto mb-6 md:mb-0">
              <Categories onCategorySelect={handleCategorySelect} handleAllItemsClick={handleAllItemsClick} newProducts={newProducts} searchQuery={searchQuery}/>
            </div>
           
            {/* Main Content */}
            <div className="flex-1 main-product-section">
              <h1 className="page-title text-2xl md:text-3xl 2xl:text-6xl font-bold font-Poppins mb-4 md:mb-6 2xl:mb-5">
                Our Variety of Products
              </h1>

              {/* Search Bar */}
              <div className="relative mb-4 md:mb-6 2xl:mb-10 opacity-100 transform translate-y-0 scale-100 animate-fade-in-up">
                <form onSubmit={handleSearchSubmit} className="relative">
                  <input
                    type="text"
                    placeholder="Search by Product Name"
                    className="w-full text-[16px] 2xl:text-2xl font-normal text-[#5F5F5F] p-3 2xl:p-6 border border-gray-300 rounded-lg pr-12 2xl:pr-16 transition-all duration-300 focus:ring-2 focus:ring-[#099CD6] focus:border-transparent focus:-translate-y-0.5 focus:shadow-lg"
                    value={searchQuery}
                    onChange={handleSearchInput}
                  />
                  <button 
                    type="submit"
                    className="absolute right-3 2xl:right-5 top-1/2 -translate-y-1/2 transition-all duration-300 hover:-translate-y-1 hover:scale-105"
                  >
                    <div className="p-1 2xl:p-3 bg-[#666666] rounded-full hover:bg-[#099CD6] transition-colors duration-300">
                      <svg
                        className="w-5 h-5 2xl:w-8 2xl:h-8 text-white"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                        />
                      </svg>
                    </div>
                  </button>
                </form>
              </div>

              <div className="product-count mb-4 md:mb-6 2xl:mb-10">
                <p className="text-[#414141] text-[16px] 2xl:text-2xl font-bold font-Poppins">
                  {productNo > 0 ? 
                    `Showing ${start} to ${end} of ${productNo} item(s)` : 
                    isLoading ? "Loading..." : "No products found"}
                </p>
              </div>

              {/* Loading and Error States */}
              {isLoading && (
                 <div style={styles.loadingContainer}>
                  <div style={styles.loadingSpinner}>
                    <div style={{...styles.spinnerRing, ...styles.ring1}}></div>
                    <div style={{...styles.spinnerRing, ...styles.ring2}}></div>
                    <div style={{...styles.spinnerRing, ...styles.ring3}}></div>
                    <div style={styles.centerText}>Ziegler</div>
                  </div>
                </div>
              )}

              {/* No Results Message */}
              {!isLoading && !error && products.length === 0 && (
                <div className="no-results text-center py-10">
                  <div className="no-results-icon mb-4">
                    <svg className="w-16 h-16 mx-auto text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.172 16.172a4 4 0 015.656 0M9 12h6m-6-4h6m2 5.291A7.962 7.962 0 0112 15c-2.034 0-3.9.785-5.291 2.09M12 3a9 9 0 11-.001 18.001A9 9 0 0112 3z" />
                    </svg>
                  </div>
                  <p className="text-lg text-gray-600 mb-4">No products found matching your criteria.</p>
                  {searchQuery && (
                    <button 
                      onClick={handleAllItemsClick} 
                      className="view-all-btn px-6 py-3 bg-[#099CD6] text-white rounded-lg hover:bg-[#0889bd] transition-all duration-300 transform hover:scale-105"
                    >
                      View All Products
                    </button>
                  )}
                </div>
              )}

              {/* Product Grid */}
              {!isLoading && !error && products.length > 0 && (
                <div className="product-grid grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-8 2xl:gap-16 scrollable-grid">
                  {products.map((product, productIndex) => (
                    // FIXED: Added check to ensure product.id exists and is valid
                    <Link 
                      to={`/subproduct/${product.id}`}
                      key={`${product.id}-${productIndex}`}
                      onClick={(e) => {
                        // FIXED: Added debug click handler to log the link destination
                        console.log(`Navigating to: /subproduct/${product.id}/${product.type}`);
                        // If product ID is invalid, prevent navigation
                        if (!product.id) {
                          e.preventDefault();
                          console.error("Invalid product ID. Navigation prevented.");
                        }
                      }}
                    >
                      <div
                        className="product-card bg-slate-200  drop-shadow-md rounded-lg p-4 md:p-6 cursor-pointer flex flex-col justify-between items-center text-center"
                        style={{
                          animationDelay: `${productIndex * 100}ms`,
                          height: "385px", 
                        }}
                      >
                        {/* Latest product and EOL badges */}
                        <div className="w-full flex justify-between mb-2">
                          {product.latest_product === true && (
                            <span className="product-badge new-badge bg-green-500 text-white text-xs px-2 py-1 rounded">New</span>
                          )}
                          {product.eol_product === true && (
                            <span className="product-badge eol-badge bg-red-500 text-white text-xs px-2 py-1 rounded">End of lifeCycle</span>
                          )}
                          {!product.latest_product && !product.eol_product && (
                            <span className="invisible">placeholder</span>
                          )}
                        </div>
                        
                        {/* Image Container */}
                        <div className="product-image-container w-full flex justify-center items-center h-[285px]">
                          {/* This outer div prevents overflow cropping */}
                          <div style={{
                            width: "242px",  // 220px + 10% for hover effect
                            height: "242px", // 220px + 10% for hover effect
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                            overflow: "hidden"
                          }}>
                            {/* This inner div holds the actual image */}
                            <div className="product-image-wrapper" style={{
                              width: "220px",
                              height: "220px",
                              overflow: "hidden",
                              display: "flex",
                              justifyContent: "center",
                              alignItems: "center",
                              transition: "transform 0.3s",
                            }}>
                              <img
                                className="product-image"
                                src={`${Constant.BASE_URL}${product.image}`}
                                alt={product.name}
                                style={{
                                  width: "220px",
                                  height: "220px",
                                  objectFit: "cover"
                                }}
                                onError={(e) => {
                                  e.target.onerror = null;
                                  e.target.src = `${Constant.BASE_URL}/placeholder-image.jpg`;
                                }}
                              />
                            </div>
                          </div>
                        </div>
                        {/* Title at the Bottom */}
                        <div className="product-info w-full mt-4 text-center">
                          <h3 className="product-title font-medium text-sm md:text-base 2xl:text-lg transition-colors duration-300 hover:text-gray-700">
                            {product.name}
                          </h3>
                          {/* Show model number if available and different from name */}
                          {product.model_name && product.model_name !== product.name && (
                            <p className="product-model text-xs text-gray-500 mt-1">
                              Model: {product.model_name}
                            </p>
                          )}
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>
              )}

              {/* Pagination */}
              {totalPages > 0 && (
                <div className="pagination-container flex justify-center mt-8">
                  <button
                    onClick={() => handlePageChange(Math.max(1, currentPage - 1))}
                    disabled={currentPage === 1}
                    className="pagination-btn mx-2 px-4 py-2 bg-gray-200 rounded disabled:opacity-50 hover:bg-gray-300 transition-all duration-300 transform hover:scale-105"
                  >
                    Previous
                  </button>
                  
                  <span className="pagination-info mx-4">
                    <progress 
                      value={currentPage} 
                      max={totalPages} 
                      className="w-full h-1 rounded-full overflow-hidden 
                        [&::-webkit-progress-bar]:bg-white 
                        [&::-webkit-progress-value]:bg-gray-500"
                    >
                    </progress>
                    <span className="mx-4">
                      Page {currentPage} of {totalPages}
                    </span>
                  </span>
                  
                  <button
                    onClick={() => handlePageChange(Math.min(totalPages, currentPage + 1))}
                    disabled={currentPage === totalPages || totalPages === 0}
                    className="pagination-btn mx-2 px-4 py-2 bg-gray-200 rounded disabled:opacity-50 hover:bg-gray-300 transition-all duration-300 transform hover:scale-105"
                  >
                    Next
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Enhanced Global Styles for Animations + Sticky Sidebar + Scrollable Grid */}
      <style jsx global>{`
        /* Sticky Sidebar - Mobile Fix */
        .sticky-sidebar {
          position: sticky;
          top: 120px; /* Adjust based on your navbar height */
          max-height: calc(100vh - 140px); /* Prevent overlap with footer */
          overflow-y: auto;
          z-index: 10;
        }

        /* Mobile responsive fix for sticky sidebar */
        @media (max-width: 768px) {
          .sticky-sidebar {
            position: static;
            top: auto;
            max-height: none;
            overflow-y: visible;
            margin-bottom: 1rem;
          }
        }

        /* Custom scrollbar for sidebar */
        .sticky-sidebar::-webkit-scrollbar {
          width: 6px;
        }
        .sticky-sidebar::-webkit-scrollbar-track {
          background: transparent;
        }
        .sticky-sidebar::-webkit-scrollbar-thumb {
          background: #099CD6;
          border-radius: 3px;
        }

        /* Scrollable Product Grid */
        .scrollable-grid {
          max-height: 80vh;
          overflow-y: auto;
          padding-right: 10px;
        }

        /* Mobile responsive fix for scrollable grid */
        @media (max-width: 768px) {
          .scrollable-grid {
            max-height: none;
            overflow-y: visible;
            padding-right: 0;
          }
        }

        /* Custom scrollbar for product grid */
        .scrollable-grid::-webkit-scrollbar {
          width: 8px;
        }
        .scrollable-grid::-webkit-scrollbar-track {
          background: #f1f1f1;
          border-radius: 4px;
        }
        .scrollable-grid::-webkit-scrollbar-thumb {
          background: #099CD6;
          border-radius: 4px;
        }
        .scrollable-grid::-webkit-scrollbar-thumb:hover {
          background: #0889bd;
        }

        /* Page Load Animations */
        .page-container {
          opacity: 0;
          transform: translateY(20px);
          transition: all 0.8s cubic-bezier(0.25, 0.46, 0.45, 0.94);
        }
        
        .page-container.page-loaded {
          opacity: 1;
          transform: translateY(0);
        }
        
        .navbar-container {
          opacity: 0;
          transform: translateY(-30px);
          animation: slideInFromTop 0.6s ease-out 0.2s forwards;
        }
        
        .main-content {
          opacity: 0;
          transform: translateY(30px);
          transition: all 0.6s ease-out;
        }
        
        .main-content.content-visible {
          opacity: 1;
          transform: translateY(0);
        }
        
        /* FIXED: Sidebar Animation - Changed to slide from left with different timing */
        .categories-sidebar {
          opacity: 0;
          transform: translateX(-50px); /* Changed from -30px to -50px for more distinction */
          animation: slideInFromLeft 0.8s ease-out 0.3s forwards; /* Different timing */
        }
        
        
        /* FIXED: Main Section Animation - Changed to slide from right with different timing */
        .main-product-section {
          opacity: 0;
          transform: translateX(50px); /* Changed from 30px to 50px for more distinction */
          animation: slideInFromRight 0.8s ease-out 0.5s forwards; /* Different timing */
        }
        
        /* FIXED: Title Animation - Changed to slide from bottom with different timing */
        .page-title {
          opacity: 0;
          transform: translateY(30px); /* Changed from 20px to 30px */
          animation: fadeInUp 0.6s ease-out 0.7s forwards; /* Different timing */
        }
        
        /* FIXED: Search Form Animation - Added scale and different direction */
        .search-form {
          opacity: 0;
          transform: translateY(-20px) scale(0.95); /* Changed to slide from top */
          animation: fadeInDownScale 0.6s ease-out 0.9s forwards; /* Different timing and animation */
        }
        
        .search-input {
          transition: all 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94);
        }
        
        .search-input:focus {
          transform: translateY(-2px);
          box-shadow: 0 8px 25px rgba(9, 156, 214, 0.15);
        }
        
        .search-button {
          transition: all 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94);
        }
        
        .search-button:hover {
          transform: translateY(-2px) scale(1.05);
        }
        
        /* Product Count Animation */
        .product-count {
          opacity: 0;
          transform: translateY(15px);
          animation: fadeInUp 0.6s ease-out 1.1s forwards; /* Adjusted timing */
        }
        
        
        /* No Results Animation */
        .no-results {
          opacity: 0;
          transform: translateY(20px);
          animation: fadeInUp 0.6s ease-out forwards;
        }
        .no-results-icon {
          animation: bounce 2s infinite;
        }
        
        .view-all-btn {
          transition: all 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94);
        }
        
        /* Product Grid Animation */
        .product-grid {
          opacity: 0;
          animation: fadeIn 0.6s ease-out 0.4s forwards;
        }
        
        .product-card {
          opacity: 0;
          transform: translateY(30px) scale(0.9);
          transition: all 0.5s cubic-bezier(0.25, 0.46, 0.45, 0.94);
          animation: productCardAppear 0.6s ease-out forwards;
        }
        
        .product-card:hover {
          transform: translateY(-8px) scale(1.02);
          box-shadow: 0 20px 40px rgba(0, 0, 0, 0.1);
        }
        
        .product-badge {
          opacity: 0;
          transform: translateY(-10px);
          animation: badgeAppear 0.4s ease-out 0.2s forwards;
        }
        
        .product-image-container {
          overflow: hidden;
          border-radius: 12px;
        }
        
        .product-image-wrapper:hover {
          transform: scale(1.1);
        }
        
        .product-image {
          transition: all 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94);
          filter: brightness(1) saturate(1);
        }
        
        // .product-card:hover .product-image {
        //   filter: brightness(1.1) saturate(1.2);
        // }
        
        .product-info {
          opacity: 0;
          transform: translateY(10px);
          animation: fadeInUp 0.4s ease-out 0.3s forwards;
        }
        
        .product-title {
          transition: all 0.3s ease;
        }
        
        .product-card:hover .product-title {
          color: #099CD6;
          transform: translateY(-2px);
        }
        
        .product-model {
          opacity: 0;
          animation: fadeIn 0.4s ease-out 0.4s forwards;
        }
        
        /* Pagination Animation */
        .pagination-container {
          opacity: 0;
          transform: translateY(20px);
          animation: fadeInUp 0.6s ease-out 0.8s forwards;
        }
        
        .pagination-btn {
          transition: all 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94);
        }
        
        .pagination-btn:not(:disabled):hover {
          background-color: #099CD6;
          color: white;
          box-shadow: 0 4px 12px rgba(9, 156, 214, 0.3);
        }
        
        .pagination-info {
          transition: all 0.3s ease;
        }
        
        /* Keyframe Animations */
        @keyframes slideInFromTop {
          from {
            opacity: 0;
            transform: translateY(-30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        @keyframes slideInFromLeft {
          from {
            opacity: 0;
            transform: translateX(-30px);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }
        
        @keyframes slideInFromRight {
          from {
            opacity: 0;
            transform: translateX(30px);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }
        
        @keyframes fadeIn {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }
        
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        @keyframes fadeInUpScale {
          from {
            opacity: 0;
            transform: translateY(20px) scale(0.95);
          }
          to {
            opacity: 1;
            transform: translateY(0) scale(1);
          }
        }
        
        @keyframes productCardAppear {
          from {
            opacity: 0;
            transform: translateY(30px) scale(0.9);
          }
          to {
            opacity: 1;
            transform: translateY(0) scale(1);
          }
        }
        
        @keyframes badgeAppear {
          from {
            opacity: 0;
            transform: translateY(-10px) scale(0.8);
          }
          to {
            opacity: 1;
            transform: translateY(0) scale(1);
          }
        }
        
        @keyframes spin {
          0% {
            transform: rotate(0deg);
          }
          100% {
            transform: rotate(360deg);
          }
        }
        
        @keyframes bounce {
          0%, 20%, 50%, 80%, 100% {
            transform: translateY(0);
          }
          40% {
            transform: translateY(-10px);
          }
          60% {
            transform: translateY(-5px);
          }
        }
        
        /* Responsive Animation Adjustments */
        @media (max-width: 768px) {
          .product-card {
            animation-delay: 0ms;
          }
          
          .categories-sidebar {
            transform: translateY(-20px);
            animation: fadeInUp 0.6s ease-out 0.4s forwards;
          }
          
          .main-product-section {
            transform: translateY(20px);
            animation: fadeInUp 0.6s ease-out 0.6s forwards;
          }
        }
        
        /* Smooth scrolling for the entire page */
        html {
          scroll-behavior: smooth;
        }
        
        /* Custom scrollbar styling */
        ::-webkit-scrollbar {
          width: 8px;
        }
        
        ::-webkit-scrollbar-track {
          background: #f1f1f1;
          border-radius: 4px;
        }
        
        ::-webkit-scrollbar-thumb {
          background: #099CD6;
          border-radius: 4px;
          transition: background 0.3s ease;
        }
        
        ::-webkit-scrollbar-thumb:hover {
          background: #0889bd;
        }
        
        /* Stagger animation for product cards */
        .product-card:nth-child(1) { animation-delay: 0.1s; }
        .product-card:nth-child(2) { animation-delay: 0.2s; }
        .product-card:nth-child(3) { animation-delay: 0.3s; }
        .product-card:nth-child(4) { animation-delay: 0.4s; }
        .product-card:nth-child(5) { animation-delay: 0.5s; }
        .product-card:nth-child(6) { animation-delay: 0.6s; }
        .product-card:nth-child(7) { animation-delay: 0.7s; }
        .product-card:nth-child(8) { animation-delay: 0.8s; }
        .product-card:nth-child(9) { animation-delay: 0.9s; }
        .product-card:nth-child(10) { animation-delay: 1.0s; }
        .product-card:nth-child(11) { animation-delay: 1.1s; }
        .product-card:nth-child(12) { animation-delay: 1.2s; }
        
        /* Accessibility: Respect user's motion preferences */
        @media (prefers-reduced-motion: reduce) {
          * {
            animation-duration: 0.01ms !important;
            animation-iteration-count: 1 !important;
            transition-duration: 0.01ms !important;
          }
        }
      `}</style>

      <Footer />
    </div>
  );
};

const styles = {
  loadingContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '60vh',
    padding: '2rem',
    textAlign: 'center',
    opacity: 0,
    animation: 'fadeIn 0.4s ease-out forwards'
  },
  loadingSpinner: {
    position: 'relative',
    width: '120px',
    height: '120px',
    margin: '0 auto',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  },
  spinnerRing: {
    position: 'absolute',
    width: '100%',
    height: '100%',
    border: '4px solid transparent',
    borderTop: '4px solid #099CD6',
    borderRadius: '50%',
    animation: 'spin 1.2s linear infinite'
  },
  ring2: {
    width: '80%',
    height: '80%',
    top: '10%',
    left: '10%',
    borderTop: '4px solid #66b3e0',
    animationDelay: '-0.4s'
  },
  ring3: {
    width: '60%',
    height: '60%',
    top: '20%',
    left: '20%',
    borderTop: '4px solid #99cceb',
    animationDelay: '-0.8s'
  },
  centerText: {
    position: 'relative',
    zIndex: 10,
    fontSize: '16px',
    fontWeight: 'bold',
    color: '#099CD6',
   
   
  },
  loadingText: {
    opacity: 0,
    animation: 'fadeIn 0.6s ease-out 0.3s forwards',
    marginTop: '20px',
    fontSize: '18px',
    color: '#666'
  }
};


export default Product;